import { Link } from "react-router-dom";

// ── Inline SVG icons ──────────────────────────────────────────────────────────

function LockIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  );
}

// ── Feature card ───────────────────────────────────────────────────────────────

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div
      className="rounded-2xl p-7 flex flex-col gap-4"
      style={{
        backgroundColor: "#133262",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ color: "#3b82c4" }}
      >
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-white text-base mb-1.5">{title}</h3>
        <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
          {description}
        </p>
      </div>
    </div>
  );
}

// ── Step ───────────────────────────────────────────────────────────────────────

function Step({ n, title, description }: { n: number; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center text-center px-4">
      <div
        className="w-10 h-10 rounded-full text-white font-bold text-sm flex items-center justify-center mb-4 shrink-0"
        style={{ backgroundColor: "#1d5199" }}
      >
        {n}
      </div>
      <h4 className="font-semibold text-white text-sm mb-1">{title}</h4>
      <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
        {description}
      </p>
    </div>
  );
}

// ── LandingPage ────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div className="font-sans min-h-screen flex flex-col">

      {/* ── SECTION 1: Hero ─────────────────────────────────────────────────── */}
      <section
        className="relative flex flex-col min-h-[90vh] bg-navy-950"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 60% 30%, #133262 0%, #0c2340 100%)",
        }}
      >
        {/* Subtle diagonal-stripe texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg, #ffffff 0px, #ffffff 1px, transparent 1px, transparent 24px)",
          }}
          aria-hidden="true"
        />

        {/* Nav */}
        <nav className="relative z-10 flex items-center justify-between px-6 py-5 max-w-5xl mx-auto w-full">
          <div className="flex items-center gap-2">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-300" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
              <line x1="12" y1="2" x2="12" y2="4" />
              <line x1="12" y1="20" x2="12" y2="22" />
              <line x1="2" y1="12" x2="4" y2="12" />
              <line x1="20" y1="12" x2="22" y2="12" />
            </svg>
            <span className="text-white font-bold text-lg tracking-tight">SeaVault</span>
          </div>
          <Link
            to="/login"
            className="text-sm font-medium text-white border border-white/30 rounded-lg px-4 py-2 hover:bg-white/10 transition-colors"
          >
            Login
          </Link>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 flex flex-col items-center justify-center flex-1 text-center px-6 pb-16">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
            <span className="text-xs font-medium text-white/80 tracking-wide uppercase">
              ISM &amp; ISPS compliant
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight max-w-2xl mb-5">
            Secure ship-to-shore document transfer and messaging
          </h1>

          <p className="text-base sm:text-lg text-navy-200 max-w-xl mb-10 leading-relaxed">
            ISM/ISPS compliant. No software to install. Works on VSAT.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs sm:max-w-none sm:w-auto justify-center">
            <Link
              to="/request-access"
              className="rounded-xl bg-white text-navy-950 px-7 py-3 text-sm font-semibold hover:bg-navy-50 transition-colors text-center"
            >
              Request Access
            </Link>
            <Link
              to="/login"
              className="rounded-xl bg-navy-700 text-white px-7 py-3 text-sm font-semibold hover:bg-navy-800 transition-colors text-center"
            >
              Login →
            </Link>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: Feature cards ─────────────────────────────────────────── */}
      <section className="py-20 px-6" style={{ backgroundColor: "#0c2340" }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-3">
            Built for maritime operations
          </h2>
          <p className="text-sm text-center mb-12" style={{ color: "rgba(255,255,255,0.5)" }}>
            Everything a shipping company and its fleet needs. Nothing it doesn't.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<LockIcon />}
              title="Secure File Transfer"
              description="Send certificates, audit reports and crew documents to any vessel. Every transfer logged, timestamped and auditable."
            />
            <FeatureCard
              icon={<ChatIcon />}
              title="Two-Way Messaging"
              description="Shore office and vessel communicate in a single auditable thread. Vessel crew reply directly from their browser — no app needed."
            />
            <FeatureCard
              icon={<ClipboardIcon />}
              title="ISM/ISPS Audit Trail"
              description="Every upload, download and message is recorded. Export the full audit log as CSV for any inspection or internal review."
            />
          </div>
        </div>
      </section>

      {/* ── SECTION 3: How it works ──────────────────────────────────────────── */}
      <section className="py-20 px-6" style={{ backgroundColor: "#0c2340" }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-3">
            How it works
          </h2>
          <p className="text-sm text-center mb-14" style={{ color: "rgba(255,255,255,0.5)" }}>
            Three steps from shore office to bridge.
          </p>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
            {/* Connector line on desktop */}
            <div
              className="hidden md:block absolute top-5 left-[calc(100%/6)] right-[calc(100%/6)] h-px"
              style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
              aria-hidden="true"
            />

            <Step
              n={1}
              title="Shore office uploads or sends"
              description="Upload a document or compose a message from the shore dashboard. Assign it to a vessel in seconds."
            />
            <Step
              n={2}
              title="Vessel receives a notification"
              description="The vessel's contact email gets an instant notification. Log in to the vessel portal — browser only, no app needed."
            />
            <Step
              n={3}
              title="Master opens, downloads or replies"
              description="The officer opens the browser, downloads files or replies to messages. Every action is logged instantly."
            />
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="bg-navy-950 py-10 px-6 mt-auto">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-bold text-white text-sm">SeaVault</p>
            <p className="text-xs text-navy-300 mt-0.5">Maritime secure communications</p>
          </div>
          <p className="text-xs text-navy-400">
            © 2026 SeaVault · seavault.uk
          </p>
        </div>
      </footer>

    </div>
  );
}
